import type {
  CreateProductPayload,
  ModifierOptionRow,
  ModifierWithOptionsResult,
  ProductByIdResult,
  ProductRow,
  ProductsListResult,
  ProductWithVariantsAndModifiersResult,
  VariantRow,
} from "@/modules/catalog/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type CreateVariantPayload = {
  name: string;
  sku?: string | null;
  barcode?: string | null;
  basePriceLak: number;
};

export type ReorderModifierLinksPayload = {
  items: Array<{ modifierId: string; sortOrder: number }>;
};

export const catalogProductsApi = {
  async listRetailVariants(): Promise<
    Array<{ id: string; name: string; productName: string }>
  > {
    return fetcher.get<Array<{ id: string; name: string; productName: string }>>(
      `${config.apiUrl}/catalog/products/retail-variants`,
    );
  },

  async listRetailVariantsLookup(params: {
    search?: string;
    page: number;
    limit?: number;
  }): Promise<{
    items: Array<{ id: string; name: string; productName: string }>;
    nextPage: number | null;
  }> {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("page", String(params.page));
    if (params.limit) searchParams.set("limit", String(params.limit));
    return fetcher.get(
      `${config.apiUrl}/catalog/products/retail-variants/lookup?${searchParams.toString()}`,
    );
  },

  async getRetailVariant(
    id: string,
  ): Promise<{ id: string; name: string; productName: string } | null> {
    try {
      return await fetcher.get<{
        id: string;
        name: string;
        productName: string;
      }>(`${config.apiUrl}/catalog/products/retail-variants/${id}`);
    } catch {
      return null;
    }
  },

  async list(query: OffsetPageQueryDTO): Promise<ProductsListResult> {
    const url = new URL(`${config.apiUrl}/catalog/products`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ProductsListResult>(url.toString());
  },

  async get(id: string): Promise<ProductByIdResult> {
    return fetcher.get<ProductByIdResult>(
      `${config.apiUrl}/catalog/products/${id}`,
    );
  },

  async getFull(
    id: string,
  ): Promise<ProductWithVariantsAndModifiersResult> {
    return fetcher.get<ProductWithVariantsAndModifiersResult>(
      `${config.apiUrl}/catalog/products/${id}/full`,
    );
  },

  async create(payload: CreateProductPayload): Promise<ProductRow> {
    return fetcher.post<ProductRow>(
      `${config.apiUrl}/catalog/products`,
      payload,
    );
  },

  async update(id: string, payload: CreateProductPayload): Promise<ProductRow> {
    return fetcher.put<ProductRow>(
      `${config.apiUrl}/catalog/products/${id}`,
      payload,
    );
  },

  async remove(id: string): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/catalog/products/${id}`,
    );
  },

  async createVariant(
    productId: string,
    payload: CreateVariantPayload,
  ): Promise<VariantRow> {
    return fetcher.post<VariantRow>(
      `${config.apiUrl}/catalog/products/${productId}/variants`,
      payload,
    );
  },

  async updateVariant(
    productId: string,
    variantId: string,
    payload: CreateVariantPayload,
  ): Promise<VariantRow> {
    return fetcher.put<VariantRow>(
      `${config.apiUrl}/catalog/products/${productId}/variants/${variantId}`,
      payload,
    );
  },

  async removeVariant(
    productId: string,
    variantId: string,
  ): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/catalog/products/${productId}/variants/${variantId}`,
    );
  },

  async addModifierLink(
    productId: string,
    modifierId: string,
  ): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${config.apiUrl}/catalog/products/${productId}/modifier-links`,
      { modifierId },
    );
  },

  async removeModifierLink(
    productId: string,
    modifierId: string,
  ): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/catalog/products/${productId}/modifier-links/${modifierId}`,
    );
  },

  async reorderModifierLinks(
    productId: string,
    payload: ReorderModifierLinksPayload,
  ): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(
      `${config.apiUrl}/catalog/products/${productId}/modifier-links/reorder`,
      payload,
    );
  },
};

export type CreateModifierPayload = {
  name: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
};

export type CreateModifierOptionPayload = {
  name: string;
  priceDeltaLak: number;
  costDeltaLak?: number | null;
};

export const catalogModifiersApi = {
  async list(): Promise<
    Array<{
      id: string;
      name: string;
      minSelect: number;
      maxSelect: number;
      isRequired: boolean;
      optionCount: number;
    }>
  > {
    return fetcher.get(`${config.apiUrl}/catalog/modifiers`);
  },

  async get(id: string): Promise<ModifierWithOptionsResult> {
    return fetcher.get<ModifierWithOptionsResult>(
      `${config.apiUrl}/catalog/modifiers/${id}`,
    );
  },

  async create(
    payload: CreateModifierPayload,
  ): Promise<{ id: string; name: string; minSelect: number; maxSelect: number; isRequired: boolean }> {
    return fetcher.post(
      `${config.apiUrl}/catalog/modifiers`,
      payload,
    );
  },

  async update(
    id: string,
    payload: CreateModifierPayload,
  ): Promise<{ id: string; name: string; minSelect: number; maxSelect: number; isRequired: boolean }> {
    return fetcher.put(
      `${config.apiUrl}/catalog/modifiers/${id}`,
      payload,
    );
  },

  async remove(id: string): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/catalog/modifiers/${id}`,
    );
  },

  async createOption(
    modifierId: string,
    payload: CreateModifierOptionPayload,
  ): Promise<ModifierOptionRow> {
    return fetcher.post<ModifierOptionRow>(
      `${config.apiUrl}/catalog/modifiers/${modifierId}/options`,
      payload,
    );
  },

  async updateOption(
    modifierId: string,
    optionId: string,
    payload: CreateModifierOptionPayload,
  ): Promise<ModifierOptionRow> {
    return fetcher.put<ModifierOptionRow>(
      `${config.apiUrl}/catalog/modifiers/${modifierId}/options/${optionId}`,
      payload,
    );
  },

  async deleteOption(
    modifierId: string,
    optionId: string,
  ): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/catalog/modifiers/${modifierId}/options/${optionId}`,
    );
  },
};
